import React from 'react';

import {assertAndContinue} from 'Asserts';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import columnToTypeIcon from 'data-frames/util/columnToTypeIcon';
import getUserColumns from 'data-frames/util/getUserColumns';
import {FUNCTION_ICON} from 'notebook/tables/excess/FunctionCategory';
import {analysisFunctions} from 'pipelines/formula/functionUtils';
import AttachDropdown from 'ui-library/attach-dropdown/AttachDropdown';
import {CustomAttachDropdownOption} from 'ui-library/attach-dropdown/AttachDropdownOption';

const FUNCTION_SECTION_HEADER = 'FUNCTIONS';
const COLUMN_SECTION_HEADER = 'COLUMNS';
const FUNCTION_TYPE = 'FUNCTION';
const COLUMN_TYPE = 'COLUMN';

export interface AnalysisFormulaSearchDropdownProps {
  dataFrame: DataFrameSpec;
  onSelectColumn: (columnSystemName: string) => void;
  onSelectFunction: (functionName: string) => void;
  query: string;
}

const AnalysisFormulaSearchDropdown = React.forwardRef<AttachDropdown, AnalysisFormulaSearchDropdownProps>(
  (props: AnalysisFormulaSearchDropdownProps, ref) => {
    // We hack onto the existing AttachDropdownItem type for our results in the following ways:
    //   1. item is always an empty object, as we don't use it
    //   2. For section headers, both the item_id and name are the section header title
    //   3. For functions and columns, the item_id is the unique identifier for that function/column, and the
    //      name is the type (either FUNCTION or COLUMN)
    const results = [];
    const query = props.query.toLowerCase();

    const matchingColumns = getUserColumns(props.dataFrame)
      .filter((column) => column.displayName.toLowerCase().startsWith(query))
      .map((column) => ({item_id: column.systemName, name: COLUMN_TYPE, item: {}}));
    if (query.length >= 3) {
      // Sort matches anywhere below matches at the start
      matchingColumns.push(
        ...getUserColumns(props.dataFrame)
          .filter(
            (column) =>
              !column.displayName.toLowerCase().startsWith(query) &&
              column.displayName.toLowerCase().includes(query)
          )
          .map((column) => ({item_id: column.systemName, name: COLUMN_TYPE, item: {}}))
      );
    }
    if (matchingColumns.length > 0) {
      results.push({item_id: COLUMN_SECTION_HEADER, name: COLUMN_SECTION_HEADER, item: {}});
      results.push(...matchingColumns);
    }

    const matchingAnalysisFunctions = analysisFunctions
      .filter((func) => func.name.toLowerCase().startsWith(props.query.toLowerCase()))
      .map((func) => ({
        item_id: func.name,
        name: FUNCTION_TYPE,
        item: {},
      }));
    if (matchingAnalysisFunctions.length > 0) {
      results.push({item_id: FUNCTION_SECTION_HEADER, name: FUNCTION_SECTION_HEADER, item: {}});
      results.push(...matchingAnalysisFunctions);
    }

    return (
      <AttachDropdown
        getRenderOptionInfo={(result) => {
          switch (result.name) {
            case FUNCTION_SECTION_HEADER:
              return {
                optionContent: <CustomAttachDropdownOption title={FUNCTION_SECTION_HEADER} />,
                hoverable: true,
                clickable: false,
              };
            case FUNCTION_TYPE: {
              const analysisFunc = analysisFunctions.find((func) => func.name === result.item_id)!;
              return {
                optionContent: (
                  <CustomAttachDropdownOption
                    title={analysisFunc.name}
                    icon={FUNCTION_ICON[analysisFunc.category]}
                    metadata={analysisFunc.shortDescription}
                  />
                ),
                hoverable: true,
                clickable: true,
              };
            }
            case COLUMN_SECTION_HEADER:
              return {
                optionContent: <CustomAttachDropdownOption title={COLUMN_SECTION_HEADER} />,
                hoverable: true,
                clickable: false,
              };
            default: {
              assertAndContinue(
                result.name === COLUMN_TYPE,
                'Invalid result in AnalysisFormulaSearchDropdown',
                {resultName: result.name}
              );
              const column = props.dataFrame.columns.find((column) => column.systemName === result.item_id)!;
              return {
                optionContent: (
                  <CustomAttachDropdownOption title={column.displayName} icon={columnToTypeIcon(column)} />
                ),
                hoverable: true,
                clickable: true,
              };
            }
          }
        }}
        noResultsMessage={`No functions or columns match "${props.query}"`}
        query={props.query}
        ref={ref}
        results={results}
        searching={false}
        /* eslint-disable-next-line camelcase */
        selectAttachment={(item_id: string, name: string) => {
          if (name === FUNCTION_TYPE) {
            props.onSelectFunction(item_id);
          } else {
            props.onSelectColumn(item_id);
          }
        }}
      />
    );
  }
);
export default AnalysisFormulaSearchDropdown;
